/**
 * Monthly Inventory Count (27 CFR 25.294) — brewery cooler FG + bulk in tanks.
 * Completing a count locks it and shows a differences summary. It never
 * changes inventory; any correction is a separate manual adjustment.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { centralToday } from '../utils/centralDate';

const API = `${API_BASE_URL}/api/monthly-inventory-counts`;

/** Stored status 'posted' is shown as "completed". */
export function statusLabel(status?: string | null): string {
  return status === 'posted' ? 'completed' : String(status || '');
}

/** Packaged lines where counted differs from book. */
export function countDifferences(lines: CountLine[]): CountLine[] {
  return lines.filter(
    (l) =>
      l.lineKind !== 'bulk' &&
      l.countedQty != null &&
      l.diffBbl != null &&
      Math.abs(Number(l.diffBbl)) >= 0.005
  );
}

const SITES = [
  { siteId: 'BR-AL-20019', name: 'Madison Brewery' },
  { siteId: 'BR-AL-20088', name: 'Athens Brewery' },
];

type CountLine = {
  id: number;
  lineKind: 'packaged' | 'bulk' | string;
  itemName?: string | null;
  packageType?: string | null;
  batchId?: string | null;
  tankName?: string | null;
  bookQty: number;
  bookBbl: number;
  countedQty: number | null;
  countedBbl: number | null;
  diffQty: number | null;
  diffBbl: number | null;
  explanation?: string | null;
};

type CountDoc = {
  id: number;
  ref: string;
  siteId: string;
  monthKey: string;
  countDate: string;
  countTime?: string | null;
  status: 'draft' | 'posted' | string;
  notes?: string | null;
  signerName?: string | null;
  signerTitle?: string | null;
  signedAt?: string | null;
  perjuryStatement: string;
  readOnly?: boolean;
  lines: CountLine[];
  summary?: {
    differenceCount?: number;
    overageBbl?: number;
    shortageBbl?: number;
  } | null;
};

type CountListRow = {
  id: number;
  ref: string;
  siteId: string;
  monthKey: string;
  countDate: string;
  countTime?: string | null;
  status: string;
  signerName?: string | null;
  postedAt?: string | null;
};

/** 'HH:MM' (Central) → '4:00 PM'; no time → end of the count date. */
export function countTimeLabel(time?: string | null): string {
  const m = String(time || '').match(/^(\d{2}):(\d{2})/);
  if (!m) return 'end of day';
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

/** Confirm, then DELETE a draft count. Returns true when deleted. */
async function deleteDraft(id: number, ref: string): Promise<boolean> {
  if (
    !window.confirm(
      `Delete draft ${ref}? This removes the draft and its counted lines. Nothing is posted to inventory, TTB, or ABC. This cannot be undone.`
    )
  ) {
    return false;
  }
  const res = await apiFetch(`${API}/${id}`, { method: 'DELETE' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Delete failed (${res.status})`);
  return true;
}

function fmt(n: number | null | undefined) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(2);
}

function siteName(siteId: string) {
  return SITES.find((s) => s.siteId === siteId)?.name || siteId;
}

export const MonthlyInventoryCountList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CountListRow[]>([]);
  const [siteId, setSiteId] = useState('BR-AL-20019');
  const [countDate, setCountDate] = useState(centralToday());
  const [countTime, setCountTime] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}?siteId=${encodeURIComponent(siteId)}`);
      const body = await res.json().catch(() => []);
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setRows(Array.isArray(body) ? body : []);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load counts');
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await apiFetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId, countDate, countTime: countTime || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Create failed (${res.status})`);
      navigate(`/monthly-inventory-counts/${body.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create count');
    } finally {
      setCreating(false);
    }
  };

  const removeDraft = async (row: CountListRow) => {
    setDeletingId(row.id);
    setError(null);
    try {
      if (await deleteDraft(row.id, row.ref)) await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete draft');
    } finally {
      setDeletingId(null);
    }
  };

  const byMonth = useMemo(() => {
    const map = new Map<string, CountListRow[]>();
    for (const row of rows) {
      const list = map.get(row.monthKey) || [];
      list.push(row);
      map.set(row.monthKey, list);
    }
    const out: [string, CountListRow[]][] = [];
    map.forEach((list, month) => {
      out.push([month, list]);
    });
    return out;
  }, [rows]);

  return (
    <div className="page-container container" data-testid="mic-list">
      <h2 className="app-header">Monthly Inventory Count</h2>
      <p>
        Physical inventory of brewery cooler finished goods and bulk beer in tanks (27 CFR 25.294).
        Taproom stock is excluded. Completed counts are view/print only and never change inventory.
      </p>

      <div className="flow-field-grid" style={{ marginBottom: 16 }}>
        <div className="flow-field">
          <label htmlFor="mic-site">Site</label>
          <select
            id="mic-site"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            data-testid="mic-site"
          >
            {SITES.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flow-field">
          <label htmlFor="mic-date">Count date</label>
          <input
            id="mic-date"
            type="date"
            value={countDate}
            onChange={(e) => setCountDate(e.target.value)}
            data-testid="mic-date"
          />
        </div>
        <div className="flow-field">
          <label htmlFor="mic-time">Count time (Central)</label>
          <input
            id="mic-time"
            type="time"
            value={countTime}
            onChange={(e) => setCountTime(e.target.value)}
            data-testid="mic-time"
          />
        </div>
      </div>
      <p className="flow-hint">
        Book quantities are what was on hand at the count date and time, so orders, transfers and
        packaging before that time are already counted. Leave the time blank for now (today) or the
        end of the day (another date).
      </p>
      <button type="button" onClick={create} disabled={creating} data-testid="mic-create">
        {creating ? 'Starting…' : 'Start monthly count'}
      </button>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {byMonth.map(([month, list]) => (
        <section key={month} style={{ marginTop: 24 }}>
          <h3>{month}</h3>
          <ul className="flow-card-list">
            {list.map((row: CountListRow) => (
              <li key={row.id}>
                <Link to={`/monthly-inventory-counts/${row.id}`} className="flow-card tappable">
                  <p className="flow-card-title">
                    {row.ref} · {siteName(row.siteId)}
                  </p>
                  <p className="flow-card-meta">
                    {row.countDate} {countTimeLabel(row.countTime)} · {statusLabel(row.status)}
                    {row.signerName ? ` · ${row.signerName}` : ''}
                  </p>
                </Link>
                {row.status === 'draft' && (
                  <button
                    type="button"
                    className="no-print"
                    onClick={() => void removeDraft(row)}
                    disabled={deletingId === row.id}
                    data-testid={`mic-delete-${row.id}`}
                    style={{ marginTop: 4 }}
                  >
                    {deletingId === row.id ? 'Deleting…' : 'Delete draft'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {!rows.length && <p className="flow-hint">No counts for this site yet.</p>}
    </div>
  );
};

/**
 * Change a draft's count date/time. The server rebuilds the book lines (tank
 * and packaged) for the new moment and keeps any counted values entered.
 */
const CountMomentEditor: React.FC<{
  countDate: string;
  countTime: string | null;
  saving: boolean;
  onSave: (countDate: string, countTime: string | null) => Promise<boolean>;
}> = ({ countDate, countTime, saving, onSave }) => {
  const [date, setDate] = useState(String(countDate).slice(0, 10));
  const [time, setTime] = useState(countTime || '');
  useEffect(() => {
    setDate(String(countDate).slice(0, 10));
    setTime(countTime || '');
  }, [countDate, countTime]);
  const changed = date !== String(countDate).slice(0, 10) || (time || null) !== (countTime || null);
  return (
    <div className="flow-row no-print" data-testid="mic-moment-editor">
      <div className="flow-field">
        <label htmlFor="mic-edit-date">Count date</label>
        <input id="mic-edit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="flow-field">
        <label htmlFor="mic-edit-time">Count time (Central)</label>
        <input id="mic-edit-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </div>
      <button
        type="button"
        disabled={!changed || saving || !date}
        onClick={() => void onSave(date, time || null)}
        data-testid="mic-moment-save"
      >
        Update count date/time
      </button>
      <p className="flow-hint">
        Changing the date or time refreshes the book quantities to what was on hand then. Counted
        quantities already entered are kept. Blank time means the end of the day.
      </p>
    </div>
  );
};

export const MonthlyInventoryCountDetail: React.FC = () => {
  const { countId } = useParams<{ countId: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<CountDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await apiFetch(`${API}/${countId}`);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
    setDoc(body as CountDoc);
  }, [countId]);

  useEffect(() => {
    load().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : 'Failed to load count')
    );
  }, [load]);

  const editable = doc?.status === 'draft';

  // Returns true on success. On error the typed values stay in place (doc is
  // only replaced by a successful response) and the server message shows.
  const patch = async (body: Record<string, unknown>): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`${API}/${countId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const next = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(next.error || `Save failed (${res.status})`);
      setDoc(next as CountDoc);
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateLine = (line: CountLine, field: 'countedQty' | 'countedBbl' | 'explanation', value: string) => {
    if (!doc || !editable) return;
    const lines = doc.lines.map((l) => {
      if (l.id !== line.id) return l;
      if (field === 'explanation') return { ...l, explanation: value };
      if (line.lineKind === 'packaged') {
        const qty = value === '' ? null : Number(value);
        if (qty == null) return { ...l, countedQty: null, countedBbl: null, diffQty: null, diffBbl: null };
        const perUnit = Number(l.bookQty) ? Number(l.bookBbl) / Number(l.bookQty) : null;
        return {
          ...l,
          countedQty: qty,
          diffQty: qty - Number(l.bookQty),
          diffBbl: perUnit == null ? l.diffBbl : (qty - Number(l.bookQty)) * perUnit,
        };
      }
      const bbl = value === '' ? null : Number(value);
      return { ...l, countedBbl: bbl, countedQty: bbl, diffQty: bbl == null ? null : 0, diffBbl: bbl == null ? null : 0 };
    });
    setDoc({ ...doc, lines });
  };

  const saveLines = () => {
    if (!doc) return;
    void patch({
      lines: doc.lines.map((l) => ({
        id: l.id,
        countedQty: l.countedQty,
        countedBbl: l.countedBbl,
        explanation: l.explanation,
      })),
      signerName: doc.signerName,
      signerTitle: doc.signerTitle,
    });
  };

  const signAndSave = () => {
    if (!doc) return;
    if (!String(doc.signerName || '').trim() || !String(doc.signerTitle || '').trim()) {
      setError('Enter a name and title to sign.');
      return;
    }
    void patch({
      lines: doc.lines.map((l) => ({
        id: l.id,
        countedQty: l.countedQty,
        countedBbl: l.countedBbl,
        explanation: l.explanation,
      })),
      signerName: doc.signerName,
      signerTitle: doc.signerTitle,
      sign: true,
    });
  };

  const post = async () => {
    if (!doc) return;
    const diffs = countDifferences(doc.lines);
    const msg = `Complete ${doc.ref}? ${
      diffs.length ? `${diffs.length} line(s) differ from book.` : 'No differences from book.'
    } The count will be locked. Inventory is not changed; adjust it manually if needed.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await patch({
        lines: doc.lines.map((l) => ({
          id: l.id,
          countedQty: l.countedQty,
          countedBbl: l.countedBbl,
          explanation: l.explanation,
        })),
        signerName: doc.signerName,
        signerTitle: doc.signerTitle,
        sign: true,
      });
      if (!saved) return;
      const res = await apiFetch(`${API}/${countId}/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Complete failed (${res.status})`);
      setDoc(body as CountDoc);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Complete failed');
    } finally {
      setSaving(false);
    }
  };

  const removeDraft = async () => {
    if (!doc) return;
    setSaving(true);
    setError(null);
    try {
      if (await deleteDraft(doc.id, doc.ref)) {
        navigate('/monthly-inventory-counts');
        return;
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete draft');
    }
    setSaving(false);
  };

  const printCount = () => {
    document.body.classList.add('mic-printing');
    const cleanup = () => {
      document.body.classList.remove('mic-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  if (!doc && !error) return <div className="page-container container">Loading…</div>;
  if (error && !doc) {
    return (
      <div className="page-container container">
        <p className="error">{error}</p>
        <Link to="/monthly-inventory-counts">Back</Link>
      </div>
    );
  }
  if (!doc) return null;

  const diffs = countDifferences(doc.lines);
  const totalDiffQty = diffs.reduce((s, l) => s + (Number(l.countedQty) - (Number(l.bookQty) || 0)), 0);
  const netDiffBbl = diffs.reduce((s, l) => s + (Number(l.diffBbl) || 0), 0);

  return (
    <div className="page-container container" data-testid="mic-detail">
      <div className="flow-toolbar no-print" style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => navigate('/monthly-inventory-counts')}>
          Back
        </button>
        <button type="button" onClick={printCount} data-testid="mic-print">
          Print
        </button>
        {editable && (
          <>
            <button type="button" onClick={saveLines} disabled={saving} data-testid="mic-save">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={signAndSave} disabled={saving} data-testid="mic-sign">
              Sign
            </button>
            <button type="button" onClick={post} disabled={saving || doc.lines.some((l) => l.countedQty == null)} data-testid="mic-post">
              Complete count
            </button>
            <button type="button" onClick={removeDraft} disabled={saving} data-testid="mic-delete">
              Delete draft
            </button>
          </>
        )}
      </div>

      {error && (
        <p className="error no-print" role="alert">
          {error}
        </p>
      )}

      <article className="mic-page" data-testid="mic-page">
        <h2>
          Monthly Inventory Count — {siteName(doc.siteId)}
        </h2>
        <p>
          <strong>{doc.ref}</strong> · {doc.monthKey} · counted {doc.countDate}{' '}
          {countTimeLabel(doc.countTime)} (Central) · {statusLabel(doc.status)}
        </p>
        {editable && (
          <CountMomentEditor
            countDate={doc.countDate}
            countTime={doc.countTime || null}
            saving={saving}
            onSave={(countDate, countTime) => patch({ countDate, countTime })}
          />
        )}
        <p className="flow-hint">27 CFR 25.294 — brewery cooler packaged beer and bulk beer in tanks (bbl). Taproom excluded.</p>
        <p className="flow-hint">
          Tank lines: enter what is in the tank. Brew size is an estimate, so a lower tank volume is
          trub loss, not a shortage, so tanks never show a difference.
        </p>

        <table className="ttb-report__table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th>Item / batch</th>
              <th>Kind</th>
              <th className="ttb-report__num">Book</th>
              <th className="ttb-report__num">Counted</th>
              <th className="ttb-report__num">Diff (bbl)</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((line) => (
              <tr key={line.id} data-testid={`mic-line-${line.id}`}>
                <td>
                  {line.itemName}
                  {line.packageType ? ` (${line.packageType})` : ''}
                  {line.batchId ? ` · ${line.batchId}` : ''}
                  {line.tankName ? ` @ ${line.tankName}` : ''}
                </td>
                <td>{line.lineKind}</td>
                <td className="ttb-report__num">
                  {line.lineKind === 'packaged'
                    ? `${fmt(line.bookQty)} u / ${fmt(line.bookBbl)} bbl`
                    : `~${fmt(line.bookBbl)} bbl est.`}
                </td>
                <td className="ttb-report__num">
                  {editable ? (
                    <input
                      type="number"
                      step="0.01"
                      value={
                        line.lineKind === 'packaged'
                          ? line.countedQty ?? ''
                          : line.countedBbl ?? ''
                      }
                      onChange={(e) =>
                        updateLine(
                          line,
                          line.lineKind === 'packaged' ? 'countedQty' : 'countedBbl',
                          e.target.value
                        )
                      }
                      placeholder="Enter count"
                      aria-label={`Counted ${line.itemName || line.batchId || ''}`}
                      data-testid={`mic-counted-${line.id}`}
                      style={{ width: 90 }}
                    />
                  ) : line.lineKind === 'packaged' ? (
                    `${fmt(line.countedQty)} u / ${fmt(line.countedBbl)} bbl`
                  ) : (
                    `${fmt(line.countedBbl)} bbl`
                  )}
                </td>
                <td className="ttb-report__num">
                  {line.countedQty == null ? '' : line.lineKind === 'bulk' ? '—' : fmt(line.diffBbl)}
                </td>
                <td>
                  {editable && Math.abs(Number(line.diffBbl) || 0) >= 0.005 ? (
                    <input
                      type="text"
                      value={line.explanation || ''}
                      onChange={(e) => updateLine(line, 'explanation', e.target.value)}
                      placeholder="Note (optional)"
                      data-testid={`mic-explain-${line.id}`}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    line.explanation || (Math.abs(Number(line.diffBbl) || 0) >= 0.005 ? '—' : '')
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {editable && (
          <p className="flow-hint" data-testid="mic-progress">
            Counted {doc.lines.filter((l) => l.countedQty != null).length} of {doc.lines.length} lines.
            Book is shown for reference; enter what is there (0 if none). Every line needs a count
            before completing. Completing never changes inventory.
          </p>
        )}
        <section className="mic-differences" data-testid="mic-differences" style={{ marginTop: 16 }}>
          <h3>Differences from book ({diffs.length})</h3>
          {diffs.length === 0 ? (
            <p>{doc.lines.some((l) => l.countedQty == null) ? 'Differences appear as counts are entered.' : 'Every line matches book.'}</p>
          ) : (
            <table className="ttb-report__table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="ttb-report__num">Book</th>
                  <th className="ttb-report__num">Counted</th>
                  <th className="ttb-report__num">Difference (units)</th>
                  <th className="ttb-report__num">Difference (bbl)</th>
                  <th>Explanation</th>
                </tr>
              </thead>
              <tbody>
                {diffs.map((l) => (
                  <tr key={l.id} data-testid={`mic-diff-${l.id}`}>
                    <td>
                      {l.itemName}
                      {l.packageType ? ` (${l.packageType})` : ''}
                    </td>
                    <td className="ttb-report__num">{fmt(l.bookQty)}</td>
                    <td className="ttb-report__num">{fmt(l.countedQty)}</td>
                    <td className="ttb-report__num">{fmt(Number(l.countedQty) - (Number(l.bookQty) || 0))}</td>
                    <td className="ttb-report__num">{fmt(l.diffBbl)}</td>
                    <td>{l.explanation || ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr data-testid="mic-diff-total">
                  <th>Total</th>
                  <th />
                  <th />
                  <th className="ttb-report__num">{fmt(totalDiffQty)}</th>
                  <th className="ttb-report__num">{fmt(netDiffBbl)}</th>
                  <th />
                </tr>
              </tfoot>
            </table>
          )}
          <p className="flow-hint">This summary does not change inventory. Adjust inventory manually if needed.</p>
        </section>

        <section style={{ marginTop: 24, borderTop: '1px solid #333', paddingTop: 12 }}>
          <p>
            <em>{doc.perjuryStatement}</em>
          </p>
          <div className="flow-field-grid">
            <div className="flow-field">
              <label htmlFor="mic-signer">Name</label>
              <input
                id="mic-signer"
                value={doc.signerName || ''}
                disabled={!editable}
                onChange={(e) => setDoc({ ...doc, signerName: e.target.value })}
                data-testid="mic-signer-name"
              />
            </div>
            <div className="flow-field">
              <label htmlFor="mic-title">Title</label>
              <input
                id="mic-title"
                value={doc.signerTitle || ''}
                disabled={!editable}
                onChange={(e) => setDoc({ ...doc, signerTitle: e.target.value })}
                data-testid="mic-signer-title"
              />
            </div>
            <div className="flow-field">
              <label>Date signed</label>
              <div>{doc.signedAt ? String(doc.signedAt).slice(0, 10) : '—'}</div>
            </div>
          </div>
        </section>
      </article>

      <style>{`
        @media print {
          body.mic-printing .no-print { display: none !important; }
          body.mic-printing .menu, body.mic-printing .navbar-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default MonthlyInventoryCountList;
